import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { xor } from 'lodash-es';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { checkboxesToSet, setToCheckboxes } from '~/components/AiSearchChips/AiFilterChip/chipHandlers';
import { FilterChip } from '~/components/AiSearchChips/FilterChip/FilterChip';
import { editableValue, isExcludeMode, isRangeModeOnly, multiValueGroup, parseNumbers, predicateFromNumbers, replaceMultiValue, valueLabelFor } from '~/components/AiSearchChips/intRangeChip.utils';
import { useLocaleKeys } from '~/i18n';
import { OrValueGroupChip } from '../OrValueGroupChip/OrValueGroupChip';
import { useSettingsChipTone } from '../settingsChipTone';
import { appendPercentToBounds } from '../summarizeDecimalRange';
import { setQuantifier } from './service-predicate';
import { BILLING_OCCURRENCES, BILLING_TRIGGERS, rebuildBillingOccurrences, rebuildBillingTriggers, removeBilling, rendersBillingOccurrenceChip, rendersBillingTriggerChip, selectedBillingOccurrences, selectedBillingTriggers } from './serviceBilling';
import { billingLimitCountTexts, billingTimingItems, billingTimingOf, billUpfrontOf } from './serviceBillingLabels';
import { hasSeededNameField, isBareExistenceBound, isBarePreapprovedExistence, PREAPPROVED_AMOUNT_EXISTENCE, rebuildBillUpfront, rebuildDecimalField, rebuildDescription, rebuildDiscountAmount, rebuildDiscountExpirationDate, rebuildDiscountType, rebuildLimitedTimeDiscount, rebuildMaxChargesLimit, rebuildPriceIncreaseExists, rebuildPriceIncreasePercent, rebuildPriceIncreaseStart, rebuildPriceIncreaseStartOnType, rebuildSetField, rebuildStartBillingAtDate, rebuildStartBillingAtExists, removeDiscountExpirationType, removeMaxCharges, rendersSetChip } from './serviceConditions';
import { decimalEditableValue, decimalFromInput, decimalIsEditable, decimalValueLabel, parseDecimal } from './serviceDecimal';
import { discountExpirationChipLabels, discountExpirationKind } from './serviceDiscountExpiration';
import { DISCOUNT_EXPIRATION_DATE_OPTION, discountExpirationDateBounds, discountExpirationDateFromRadio, discountExpirationDateLabel } from './serviceDiscountExpirationDate';
import { rebuildServiceNames, resolveServiceNameSelection } from './serviceName';
import { serviceOrValueGroupFields, serviceOrValueGroupTexts } from './serviceOrValueGroupFields';
import { hasServicePrice, isValidPriceRange, rebuildServicePrice, removeServicePrice, servicePriceLabel, servicePriceValue } from './servicePrice';
import { priceIncreaseStartDateBounds, priceIncreaseStartFromRadio, priceIncreaseStartSelected, priceIncreaseStartValueLabel, START_ON_ANNIVERSARY_OPTION, START_ON_DATE_OPTION, usesStartOnCheckboxEditor, usesStartOnRadioEditor } from './servicePriceIncreaseStart';
import { billingOccurrenceTextsPath, localizeBillingOccurrence, localizeBillingTrigger, localizeDiscountType, localizePricingType, localizeStartOnType } from './services-localize';
import { billingDimensionChip, billingDimensionItems, serviceChipPropsFor } from './ServicesChips.utils';
import { START_BILLING_AT_DATE_OPTION, startBillingAtDateBounds, startBillingAtDateFromRadio, startBillingAtDateLabel, startBillingAtExistsChipLabels, startBillingAtToggledExists } from './serviceStartBillingAt';
export const ServiceFlattenedChips = (param)=>{
    let { chipTestId, grammar, services, quantifier, predicate, autoOpenField, onChange } = param;
    var _ref;
    var _predicate_pricingType, _discount_expiration;
    const { texts: t, allowed, biPrefix } = grammar;
    const { t: agreementTexts } = useLocaleKeys('agreement');
    const { t: occurrenceTexts } = useLocaleKeys(billingOccurrenceTextsPath);
    const tone = useSettingsChipTone();
    const serviceOptions = grammar.useNameOptions();
    const serviceNames = useMemo(()=>serviceOptions.map((option)=>option.name), [
        serviceOptions
    ]);
    const serviceItems = useMemo(()=>serviceNames.map((name)=>({
                value: name,
                label: name
            })), [
        serviceNames
    ]);
    const numberRangeTexts = {
        apply: t.applyButton(),
        validationError: t.servicesDecimalValidationError()
    };
    const minMaxTexts = {
        apply: t.applyButton(),
        validationError: t.servicesPriceRangeValidationError(),
        minPlaceholder: t.servicesPriceMinLabel(),
        maxPlaceholder: t.servicesPriceMaxLabel()
    };
    const billingOccurrenceItems = billingDimensionItems(BILLING_OCCURRENCES, (value)=>localizeBillingOccurrence(occurrenceTexts, value));
    const selectablePricingTypes = allowed.pricingType.filter((value)=>value !== 'variable');
    const pricingTypeItems = selectablePricingTypes.map((value)=>({
            value,
            label: localizePricingType(t, value)
        }));
    const discountTypeItems = allowed.discountType.map((value)=>({
            value,
            label: localizeDiscountType(t, value)
        }));
    const startOnTypeItems = allowed.startOnType.map((value)=>({
            value,
            label: localizeStartOnType(t, value)
        }));
    const apply = (next)=>onChange(setQuantifier(services, quantifier, next));
    const chips = [];
    const renders = (field)=>grammar.fields.has(field);
    const opensOnMount = (field)=>autoOpenField === field;
    const chipProps = serviceChipPropsFor({
        predicate,
        tone,
        chipTestId,
        apply,
        numberRangeTexts
    });
    const names = resolveServiceNameSelection(predicate, serviceOptions);
    const applyNamePick = (param)=>{
        let { selected } = param;
        return apply(predicate.isBundle === true && xor(selected, names).length === 0 ? predicate : rebuildServiceNames(predicate, selected));
    };
    const rendersBundleChip = renders('isBundle') && names.length === 0 && predicate.isBundle === true;
    if (rendersBundleChip) {
        chips.push(/*#__PURE__*/ _jsx(FilterChip, {
            testId: chipTestId('name'),
            fieldLabel: t.servicesLabel(),
            valueLabel: t.servicesIsBundleLabel(),
            tone: tone,
            onRemove: ()=>apply(rebuildServiceNames(predicate, [])),
            removeBiName: "".concat(biPrefix, "_is_bundle_remove")
        }, "name"));
    }
    const rendersNameChip = names.length > 0 || hasSeededNameField(grammar, predicate);
    if (renders('name') && rendersNameChip && !rendersBundleChip) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "checkboxes",
            testId: chipTestId('name'),
            fieldLabel: t.servicesLabel(),
            items: serviceItems,
            selected: names,
            operator: "include",
            searchable: true,
            tone: tone,
            onApply: applyNamePick,
            onRemove: ()=>apply(rebuildServiceNames(predicate, [])),
            biPrefix: "".concat(biPrefix, "_name"),
            autoOpen: opensOnMount('name')
        }, "name"));
    }
    const billingOccurrences = selectedBillingOccurrences(predicate);
    if (rendersBillingOccurrenceChip(grammar, predicate)) {
        chips.push(billingDimensionChip({
            field: 'billingOccurrence',
            testId: chipTestId('billingOccurrence'),
            fieldLabel: t.servicesBillingOccurrenceLabel(),
            items: billingOccurrenceItems,
            selected: billingOccurrences,
            biPrefix: "".concat(biPrefix, "_billing_occurrence"),
            tone,
            autoOpen: opensOnMount('billingOccurrence'),
            onApply: (next)=>apply(rebuildBillingOccurrences(predicate, BILLING_OCCURRENCES.filter((value)=>next.includes(value)))),
            onRemove: ()=>apply(removeBilling(predicate))
        }));
    }
    const billingTriggers = selectedBillingTriggers(predicate);
    const offersPartlyAutomatic = billingOccurrences.includes('inStages');
    const selectableTriggers = BILLING_TRIGGERS.filter((value)=>value !== 'partlyAutomatic' || offersPartlyAutomatic);
    if (rendersBillingTriggerChip(grammar, predicate)) {
        chips.push(billingDimensionChip({
            field: 'billingTrigger',
            testId: chipTestId('billingTrigger'),
            fieldLabel: t.servicesBillingTriggerLabel(),
            items: billingDimensionItems(selectableTriggers, (value)=>localizeBillingTrigger(t, value)),
            selected: billingTriggers,
            biPrefix: "".concat(biPrefix, "_billing_trigger"),
            tone,
            autoOpen: opensOnMount('billingTrigger'),
            onApply: (next)=>apply(rebuildBillingTriggers(predicate, BILLING_TRIGGERS.filter((value)=>next.includes(value)))),
            onRemove: ()=>apply(removeBilling(predicate))
        }));
    }
    for (const stateField of grammar.stateFields){
        const value = predicate[stateField.field];
        if (!renders(stateField.field) || !rendersSetChip(grammar, stateField.field, value)) continue;
        const { selected, operator } = setToCheckboxes(value !== null && value !== void 0 ? value : {});
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "checkboxes",
            testId: chipTestId(stateField.field),
            fieldLabel: stateField.fieldLabel,
            items: stateField.items,
            selected: selected,
            operator: operator,
            tone: tone,
            onApply: (next)=>apply(rebuildSetField(predicate, stateField.field, checkboxesToSet(next))),
            onRemove: ()=>apply(rebuildSetField(predicate, stateField.field, undefined)),
            biPrefix: "".concat(biPrefix, "_").concat(stateField.biSuffix),
            autoOpen: opensOnMount(stateField.field)
        }, stateField.field));
    }
    const pricingItems = ((_ref = (_predicate_pricingType = predicate.pricingType) === null || _predicate_pricingType === void 0 ? void 0 : _predicate_pricingType.in) !== null && _ref !== void 0 ? _ref : []).includes('variable') ? [
        ...pricingTypeItems,
        {
            value: 'variable',
            label: localizePricingType(t, 'variable')
        }
    ] : pricingTypeItems;
    if (renders('pricingType') && rendersSetChip(grammar, 'pricingType', predicate.pricingType)) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, _object_spread_props(_object_spread({}, chipProps.set({
            field: 'pricingType',
            fieldLabel: t.servicesPricingTypeLabel(),
            items: pricingItems,
            biPrefix: "".concat(biPrefix, "_pricing")
        })), {
            autoOpen: opensOnMount('pricingType')
        }), "pricingType"));
    }
    if ((renders('priceMin') || renders('priceMax')) && hasServicePrice(predicate)) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "minMax",
            testId: chipTestId('servicePrice'),
            fieldLabel: t.servicesPriceLabel(),
            value: servicePriceValue(predicate),
            valueLabel: servicePriceLabel(predicate, t),
            tone: tone,
            validate: isValidPriceRange,
            onApply: (next)=>apply(rebuildServicePrice(predicate, next)),
            onRemove: ()=>apply(removeServicePrice(predicate)),
            biPrefix: "".concat(biPrefix, "_price"),
            texts: minMaxTexts
        }, "servicePrice"));
    }
    const cap = renders('cap') ? predicate.cap : undefined;
    if (cap != null) {
        chips.push(/*#__PURE__*/ _jsx(FilterChip, {
            testId: chipTestId('capExists'),
            fieldLabel: t.servicesLabel(),
            valueLabel: t.servicesPreapprovedAmountNoun(),
            tone: tone,
            onRemove: ()=>apply(rebuildDecimalField(predicate, 'cap', undefined)),
            removeBiName: "".concat(biPrefix, "_preapproved_amount_exists_remove")
        }, "capExists"));
    }
    if (cap != null && !isBarePreapprovedExistence(cap)) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, _object_spread({}, chipProps.decimal({
            field: 'cap',
            fieldLabel: t.servicesPreapprovedAmountLabel(),
            biPrefix: "".concat(biPrefix, "_cap"),
            range: cap,
            removedValue: PREAPPROVED_AMOUNT_EXISTENCE
        })), "cap"));
    }
    const discount = predicate.discount;
    if (renders('discount.type') && rendersSetChip(grammar, 'discount.type', discount === null || discount === void 0 ? void 0 : discount.type)) {
        var _ref1;
        const { selected, operator } = setToCheckboxes((_ref1 = discount === null || discount === void 0 ? void 0 : discount.type) !== null && _ref1 !== void 0 ? _ref1 : {});
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "checkboxes",
            testId: chipTestId('discountType'),
            fieldLabel: t.servicesDiscountTypeLabel(),
            items: discountTypeItems,
            selected: selected,
            operator: operator,
            tone: tone,
            onApply: (next)=>apply(rebuildDiscountType(predicate, checkboxesToSet(next))),
            onRemove: ()=>apply(rebuildDiscountType(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_discount_type"),
            autoOpen: opensOnMount('discountType')
        }, "discountType"));
    }
    if (renders('discount.amount') && (discount === null || discount === void 0 ? void 0 : discount.amount) != null && !isBareExistenceBound(discount.amount)) {
        const amount = discount.amount;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "numberRange",
            testId: chipTestId('discountAmount'),
            fieldLabel: t.servicesDiscountAmountLabel(),
            value: decimalEditableValue(amount),
            valueLabel: decimalValueLabel(amount),
            tone: tone,
            editable: decimalIsEditable(amount),
            validate: (input)=>parseDecimal(input),
            onApply: (input)=>apply(rebuildDiscountAmount(predicate, decimalFromInput(input, amount.neq != null))),
            onRemove: ()=>apply(rebuildDiscountAmount(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_discount_amount"),
            texts: numberRangeTexts
        }, "discountAmount"));
    }
    const discountExpiration = renders('discount.expiration') ? discountExpirationKind(discount === null || discount === void 0 ? void 0 : discount.expiration) : null;
    if (discountExpiration != null) {
        const { fieldLabel, valueLabel } = discountExpirationChipLabels(discountExpiration, t);
        chips.push(/*#__PURE__*/ _jsx(FilterChip, {
            testId: chipTestId('discountExpiration'),
            fieldLabel: fieldLabel,
            valueLabel: valueLabel,
            tone: tone,
            onRemove: ()=>apply(removeDiscountExpirationType(predicate)),
            removeBiName: "".concat(biPrefix, "_discount_expiration_remove")
        }, "discountExpiration"));
    }
    const discountExpirationDate = renders('discount.expiration') ? discount === null || discount === void 0 ? void 0 : (_discount_expiration = discount.expiration) === null || _discount_expiration === void 0 ? void 0 : _discount_expiration.date : undefined;
    if (discountExpirationDate != null) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "radio",
            testId: chipTestId('discountExpirationDate'),
            fieldLabel: t.servicesDiscountExpirationLabel(),
            items: [
                {
                    value: DISCOUNT_EXPIRATION_DATE_OPTION,
                    label: t.servicesDiscountExpirationDateOption()
                }
            ],
            selected: DISCOUNT_EXPIRATION_DATE_OPTION,
            valueLabel: discountExpirationDateLabel(discountExpirationDate, t),
            editable: discountExpirationDate.neq == null,
            datePicker: _object_spread_props(_object_spread({
                rangeValue: DISCOUNT_EXPIRATION_DATE_OPTION
            }, discountExpirationDateBounds(discountExpirationDate)), {
                texts: {
                    from: agreementTexts.filters.dateRange.from(),
                    to: agreementTexts.filters.dateRange.to()
                }
            }),
            tone: tone,
            onApply: (next)=>apply(rebuildDiscountExpirationDate(predicate, discountExpirationDateFromRadio(next))),
            onRemove: ()=>apply(rebuildDiscountExpirationDate(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_discount_expiration_date"),
            texts: {
                apply: t.applyButton()
            }
        }, "discountExpirationDate"));
    }
    const priceIncrease = predicate.priceIncrease;
    if (renders('priceIncrease.percent') && (priceIncrease === null || priceIncrease === void 0 ? void 0 : priceIncrease.percent) != null) {
        const percent = priceIncrease.percent;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "numberRange",
            testId: chipTestId('priceIncreasePercent'),
            fieldLabel: t.servicesPriceIncreaseLabel(),
            suffix: "%",
            value: decimalEditableValue(percent),
            valueLabel: appendPercentToBounds(decimalValueLabel(percent)),
            tone: tone,
            editable: decimalIsEditable(percent),
            validate: (input)=>parseDecimal(input),
            onApply: (input)=>apply(rebuildPriceIncreasePercent(predicate, decimalFromInput(input, percent.neq != null))),
            onRemove: ()=>apply(rebuildPriceIncreasePercent(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_price_increase_percent"),
            texts: numberRangeTexts
        }, "priceIncreasePercent"));
    }
    const rendersStartOnType = renders('priceIncrease.startOnType');
    if (rendersStartOnType && priceIncrease != null && usesStartOnCheckboxEditor(priceIncrease)) {
        var _priceIncrease_startOnType;
        const { selected, operator } = setToCheckboxes((_priceIncrease_startOnType = priceIncrease.startOnType) !== null && _priceIncrease_startOnType !== void 0 ? _priceIncrease_startOnType : {});
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "checkboxes",
            testId: chipTestId('priceIncreaseStartOnType'),
            fieldLabel: t.servicesPriceIncreaseStartOnLabel(),
            items: startOnTypeItems,
            selected: selected,
            operator: operator,
            tone: tone,
            onApply: (next)=>apply(rebuildPriceIncreaseStartOnType(predicate, checkboxesToSet(next))),
            onRemove: ()=>apply(rebuildPriceIncreaseStartOnType(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_price_increase_start_on"),
            autoOpen: opensOnMount('priceIncreaseStartOnType')
        }, "priceIncreaseStartOnType"));
    }
    const rendersStartOnDate = renders('priceIncrease.startOn');
    if ((rendersStartOnType || rendersStartOnDate) && priceIncrease != null && usesStartOnRadioEditor(priceIncrease)) {
        var _priceIncrease_startOn;
        const startOnTypeKeptByCheckboxChip = usesStartOnCheckboxEditor(priceIncrease) ? priceIncrease.startOnType : undefined;
        const applyStart = (start)=>{
            var _ref;
            return apply(rebuildPriceIncreaseStart(predicate, {
                startOnType: (_ref = start === null || start === void 0 ? void 0 : start.startOnType) !== null && _ref !== void 0 ? _ref : startOnTypeKeptByCheckboxChip,
                startOn: start === null || start === void 0 ? void 0 : start.startOn
            }));
        };
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "radio",
            testId: chipTestId('priceIncreaseStartOn'),
            fieldLabel: t.servicesPriceIncreaseStartOnLabel(),
            items: [
                {
                    value: START_ON_ANNIVERSARY_OPTION,
                    label: t.servicesStartOnType.yearlyEffectiveDate()
                },
                {
                    value: START_ON_DATE_OPTION,
                    label: t.servicesPriceIncreaseStartOnDateOption()
                }
            ],
            selected: priceIncreaseStartSelected(priceIncrease),
            valueLabel: priceIncreaseStartValueLabel(priceIncrease, t),
            editable: ((_priceIncrease_startOn = priceIncrease.startOn) === null || _priceIncrease_startOn === void 0 ? void 0 : _priceIncrease_startOn.neq) == null,
            datePicker: rendersStartOnDate ? _object_spread_props(_object_spread({
                rangeValue: START_ON_DATE_OPTION
            }, priceIncreaseStartDateBounds(priceIncrease.startOn)), {
                texts: {
                    from: agreementTexts.filters.dateRange.from(),
                    to: agreementTexts.filters.dateRange.to()
                }
            }) : undefined,
            tone: tone,
            onApply: (next)=>applyStart(priceIncreaseStartFromRadio(next)),
            onRemove: ()=>applyStart(undefined),
            biPrefix: "".concat(biPrefix, "_price_increase_start_on_date"),
            texts: {
                apply: t.applyButton()
            }
        }, "priceIncreaseStartOn"));
    }
    if (renders('priceIncrease.exists') && (priceIncrease === null || priceIncrease === void 0 ? void 0 : priceIncrease.exists) != null) {
        const exists = priceIncrease.exists;
        chips.push(/*#__PURE__*/ _jsx(FilterChip, {
            testId: chipTestId('priceIncreaseExists'),
            fieldLabel: t.servicesHasLabel(),
            valueLabel: exists ? t.servicesPriceIncreaseAnnualNoun() : t.servicesPriceIncreaseNoneNoun(),
            tone: tone,
            onRemove: ()=>apply(rebuildPriceIncreaseExists(predicate, undefined)),
            removeBiName: "".concat(biPrefix, "_price_increase_exists_remove")
        }, "priceIncreaseExists"));
    }
    const limitedTimeDiscountOffer = renders('limitedTimeDiscountOffer') ? predicate.limitedTimeDiscountOffer : undefined;
    if ((limitedTimeDiscountOffer === null || limitedTimeDiscountOffer === void 0 ? void 0 : limitedTimeDiscountOffer.exists) != null) {
        const exists = limitedTimeDiscountOffer.exists;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "toggle",
            testId: chipTestId('limitedTimeDiscountOffer'),
            fieldLabel: t.servicesConditionalDiscountExistsLabel(),
            valueLabel: exists ? t.servicesConditionalDiscountExistsLabel() : t.servicesConditionalDiscountNoneLabel(),
            tone: tone,
            onToggle: ()=>apply(rebuildLimitedTimeDiscount(predicate, !exists)),
            onRemove: ()=>apply(rebuildLimitedTimeDiscount(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_conditional_discount")
        }, "limitedTimeDiscountOffer"));
    }
    const maxCharges = renders('maxCharges') ? predicate.maxCharges : undefined;
    if (maxCharges != null) {
        const hasLimitedCycles = maxCharges.exists !== false;
        chips.push(/*#__PURE__*/ _jsx(FilterChip, {
            testId: chipTestId('maxChargesExists'),
            fieldLabel: t.servicesHasLabel(),
            valueLabel: hasLimitedCycles ? t.servicesBillingLimitCyclesNoun() : t.servicesBillingLimitNoCyclesNoun(),
            tone: tone,
            onRemove: ()=>apply(removeMaxCharges(predicate)),
            removeBiName: "".concat(biPrefix, "_billing_limit_exists_remove")
        }, "maxChargesExists"));
    }
    if ((maxCharges === null || maxCharges === void 0 ? void 0 : maxCharges.limit) != null) {
        const limit = maxCharges.limit;
        const limitTexts = billingLimitCountTexts(t);
        const applyLimit = (input)=>{
            var _parseNumbers;
            return apply(rebuildMaxChargesLimit(predicate, predicateFromNumbers((_parseNumbers = parseNumbers(input)) !== null && _parseNumbers !== void 0 ? _parseNumbers : [], isExcludeMode(limit))));
        };
        const limitValues = multiValueGroup(limit, limitTexts);
        const applyLimitValue = (index, value)=>apply(rebuildMaxChargesLimit(predicate, replaceMultiValue(limit, index, value == null ? undefined : Number(value))));
        chips.push(limitValues != null ? /*#__PURE__*/ _jsx(OrValueGroupChip, {
            testId: chipTestId('maxChargesLimit'),
            field: "maxChargesLimit",
            fields: serviceOrValueGroupFields(t),
            values: limitValues,
            connectorTone: "or",
            tone: tone,
            texts: serviceOrValueGroupTexts(t, grammar.connector.labels),
            bi: {
                edit: {
                    name: "".concat(biPrefix, "_billing_limit_edit")
                },
                apply: {
                    name: "".concat(biPrefix, "_billing_limit_apply")
                },
                remove: {
                    name: "".concat(biPrefix, "_billing_limit_remove")
                }
            },
            onValueChange: applyLimitValue,
            onRemove: ()=>apply(rebuildMaxChargesLimit(predicate, undefined))
        }, "maxChargesLimit") : /*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "numberRange",
            testId: chipTestId('maxChargesLimit'),
            fieldLabel: t.servicesBillingLimitCanBeBilledLabel(),
            value: editableValue(limit),
            valueLabel: valueLabelFor(limit, limitTexts),
            tone: tone,
            editable: !isRangeModeOnly(limit),
            validate: (input)=>parseNumbers(input) != null,
            onApply: applyLimit,
            onRemove: ()=>apply(rebuildMaxChargesLimit(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_billing_limit"),
            texts: {
                apply: t.applyButton(),
                validationError: t.netDaysValidationError()
            }
        }, "maxChargesLimit"));
    }
    const billUpfront = renders('billUpfront') ? predicate.billUpfront : undefined;
    if (billUpfront != null) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "radio",
            testId: chipTestId('billUpfront'),
            fieldLabel: t.servicesBillingTimingLabel(),
            items: billingTimingItems(t),
            selected: billingTimingOf(billUpfront),
            tone: tone,
            onApply: (param)=>{
                let { selected } = param;
                return apply(rebuildBillUpfront(predicate, billUpfrontOf(selected)));
            },
            onRemove: ()=>apply(rebuildBillUpfront(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_billing_timing"),
            texts: {
                apply: t.applyButton()
            }
        }, "billUpfront"));
    }
    const description = renders('description') ? predicate.description : undefined;
    if ((description === null || description === void 0 ? void 0 : description.exists) != null) {
        const exists = description.exists;
        chips.push(/*#__PURE__*/ _jsx(FilterChip, {
            testId: chipTestId('description'),
            fieldLabel: t.servicesLabel(),
            valueLabel: exists ? t.servicesDescriptionExistsLabel() : t.servicesDescriptionNoneLabel(),
            tone: tone,
            onRemove: ()=>apply(rebuildDescription(predicate, undefined)),
            removeBiName: "".concat(biPrefix, "_description_remove")
        }, "description"));
    }
    const startBillingAt = renders('startBillingAt') ? predicate.startBillingAt : undefined;
    const startBillingAtExists = startBillingAtToggledExists(startBillingAt);
    if (startBillingAtExists != null) {
        const { fieldLabel, valueLabel } = startBillingAtExistsChipLabels(startBillingAtExists, t);
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "toggle",
            testId: chipTestId('startBillingAtExists'),
            fieldLabel: fieldLabel,
            valueLabel: valueLabel,
            tone: tone,
            onToggle: ()=>apply(rebuildStartBillingAtExists(predicate, !startBillingAtExists)),
            onRemove: ()=>apply(rebuildStartBillingAtExists(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_start_billing_at_exists")
        }, "startBillingAtExists"));
    }
    const startBillingAtDate = startBillingAt === null || startBillingAt === void 0 ? void 0 : startBillingAt.date;
    if (startBillingAtDate != null) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "radio",
            testId: chipTestId('startBillingAtDate'),
            fieldLabel: t.servicesStartBillingAtLabel(),
            items: [
                {
                    value: START_BILLING_AT_DATE_OPTION,
                    label: t.servicesStartBillingAtDateOption()
                }
            ],
            selected: START_BILLING_AT_DATE_OPTION,
            valueLabel: startBillingAtDateLabel(startBillingAtDate, t),
            editable: startBillingAtDate.neq == null,
            datePicker: _object_spread_props(_object_spread({
                rangeValue: START_BILLING_AT_DATE_OPTION
            }, startBillingAtDateBounds(startBillingAtDate)), {
                texts: {
                    from: agreementTexts.filters.dateRange.from(),
                    to: agreementTexts.filters.dateRange.to()
                }
            }),
            tone: tone,
            onApply: (next)=>apply(rebuildStartBillingAtDate(predicate, startBillingAtDateFromRadio(next))),
            onRemove: ()=>apply(rebuildStartBillingAtDate(predicate, undefined)),
            biPrefix: "".concat(biPrefix, "_start_billing_at_date"),
            texts: {
                apply: t.applyButton()
            }
        }, "startBillingAtDate"));
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: chips
    });
};
